"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

type Item = {
  id: string;
  title: string;
  city: string;
  country: string;
  photoUrl?: string;
};

const KEY = "r2l.recentlyViewed";
const MAX = 6;

/**
 * Records and displays a small carousel of recently viewed listings.
 * Stored in localStorage; no server round-trip.
 */
export function RecordRecentlyViewed({ item }: { item: Item }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(KEY);
      const list: Item[] = raw ? JSON.parse(raw) : [];
      const next = [item, ...list.filter((x) => x.id !== item.id)].slice(0, MAX);
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // localStorage might be blocked — silently ignore
    }
  }, [item]);
  return null;
}

export default function RecentlyViewed({ excludeId }: { excludeId?: string }) {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      const list: Item[] = raw ? JSON.parse(raw) : [];
      setItems(excludeId ? list.filter((x) => x.id !== excludeId) : list);
    } catch {
      setItems([]);
    }
  }, [excludeId]);

  if (items.length === 0) return null;

  return (
    <section className="px-6 py-12 max-w-6xl mx-auto">
      <h2 className="text-xl font-semibold mb-4">Recently viewed</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {items.map((it) => (
          <a key={it.id} href={`/listings/${it.id}`} className="group block">
            <div className="relative aspect-square rounded-lg overflow-hidden bg-neutral-100">
              {it.photoUrl ? (
                <Image
                  src={it.photoUrl}
                  alt={it.title}
                  fill
                  sizes="(max-width: 640px) 50vw, 16vw"
                  className="object-cover group-hover:scale-105 transition"
                  unoptimized={/^http:\/\/(localhost|127\.0\.0\.1)/.test(it.photoUrl)}
                />
              ) : null}
            </div>
            <div className="text-xs font-medium mt-1 truncate">{it.title}</div>
            <div className="text-xs text-neutral-500">{it.city}, {it.country}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
